import React, { useEffect, useRef, useState } from 'react';
import { Box, CircularProgress, Container, Divider, Typography } from '@mui/material';
import 'dayjs/locale/uk';
import { Navigate, useNavigate, useParams } from 'react-router';
import { useDispatch, useSelector } from 'react-redux';
import ModalAlert from '~/components/modals/ModalAlert/ModalAlert';
import { errorMessages } from '~/constants/errorMessages.js';
import {
  selectActiveApplications,
  selectActiveApplicationsError,
  selectActiveApplicationsPageConfiguration,
  selectActiveApplicationsStatus,
  selectApplications,
  selectApplicationsError,
  selectApplicationsPageConfiguration,
  selectApplicationsStatus,
  selectCurrentContragent,
  selectCurrentContragentError,
  selectCurrentContragentStatus,
  selectRequiredDocuments,
  selectRequiredDocumentsError,
  selectRequiredDocumentsPageConfiguration,
  selectRequiredDocumentsStatus,
} from '~/store/covenants/selectors.ts';
import { resetCurrentContragent } from '~/store/covenants/slice.ts';
import { fetchRequiredDocuments } from '~/store/covenants/thunks/documentThunks.ts';
import { commonStyles } from '~/modules/covenants/CovenantsDataForm/commonStyles.ts';
import { AppDispatch } from '~/store/store.ts';
import {
  buildCovenantsApplicationPath,
  persistCovenantsApplicationContragent,
} from '~/modules/covenants/utils.ts';
import ApplicationTableView from '~/modules/covenants/CovenantsDataForm/components/ApplicationTableView.tsx';
import { CreateApplicationModal } from '~/modules/covenants/CovenantsDataForm/CovenantsFormByClient/components/CreateApplicationModal.tsx';
import {
  createApplication,
  fetchActiveApplications,
  fetchApplications,
} from '~/store/covenants/thunks/applicationThunks.ts';
import DocumentsView from '~/modules/covenants/CovenantsDataForm/CovenantsFormByClient/CovenantsApplicationFormByClient.tsx';
import { fetchContragentById } from '~/store/covenants/thunks/contragentThunks.ts';
import { StatusFilterValue } from '~/modules/covenants/types.ts';
import { clientStyles } from '~/modules/covenants/CovenantsDataForm/CovenantsFormByClient/styles.ts';

const CovenantsApplicationByClient: React.FC = () => {
  const dispatch = useDispatch<AppDispatch>();
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();

  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [documentsPage, setDocumentsPage] = useState(0);
  const [documentsRowsPerPage, setDocumentsRowsPerPage] = useState(10);
  const [applicationsPage, setApplicationsPage] = useState(0);
  const [applicationsRowsPerPage, setApplicationsRowsPerPage] = useState(10);
  const [applicationsStatusFilter, setApplicationsStatusFilter] = useState<StatusFilterValue>(
    StatusFilterValue.All
  );
  const [activeApplicationsPage, setActiveApplicationsPage] = useState(0);
  const [modalErrorText, setModalErrorText] = useState('');
  const activeApplicationsRowsPerPage = 4;
  const previousApplicationsContragentIdRef = useRef<number | null>(null);
  const previousDocumentsContragentIdRef = useRef<number | null>(null);
  const previousActiveApplicationsContragentIdRef = useRef<number | null>(null);

  const counterparty = useSelector(selectCurrentContragent);
  const cpStatus = useSelector(selectCurrentContragentStatus);
  const cpError = useSelector(selectCurrentContragentError);

  const documents = useSelector(selectRequiredDocuments);
  const docsError = useSelector(selectRequiredDocumentsError);
  const documentsPageConfiguration = useSelector(selectRequiredDocumentsPageConfiguration);
  const docsStatus = useSelector(selectRequiredDocumentsStatus);

  const applications = useSelector(selectApplications);
  const appsError = useSelector(selectApplicationsError);
  const applicationsPageConfiguration = useSelector(selectApplicationsPageConfiguration);
  const appsStatus = useSelector(selectApplicationsStatus);
  const activeApplications = useSelector(selectActiveApplications);
  const activeAppsError = useSelector(selectActiveApplicationsError);
  const activeApplicationsPageConfiguration = useSelector(
    selectActiveApplicationsPageConfiguration
  );
  const activeAppsStatus = useSelector(selectActiveApplicationsStatus);

  const cpLoading = cpStatus === 'loading';
  const docsLoading = docsStatus === 'loading';
  const appsLoading = appsStatus === 'loading';
  const activeAppsLoading = activeAppsStatus === 'loading';
  const isInitialDocumentsLoading =
    docsLoading && !documents.length && documentsPageConfiguration.totalElements === 0;
  const isInitialApplicationsLoading =
    appsLoading && !applications.length && applicationsPageConfiguration.totalElements === 0;
  const isInitialActiveApplicationsLoading =
    activeAppsLoading &&
    !activeApplications.length &&
    activeApplicationsPageConfiguration.totalElements === 0;

  const getErrorMessage = (
    error?: { message?: string; data?: { message?: string } } | null
  ): string => error?.message || error?.data?.message || errorMessages.SERVICE_UNAVAILABLE;

  const closeErrorModal = () => {
    setModalErrorText('');
  };

  useEffect(() => {
    const nextError = cpError || docsError || appsError || activeAppsError;

    if (nextError) {
      setModalErrorText(getErrorMessage(nextError));
    }
  }, [activeAppsError, appsError, cpError, docsError]);

  useEffect(() => {
    if (!id) return;
    dispatch(fetchContragentById({ id: Number(id) }));

    return () => {
      dispatch(resetCurrentContragent());
    };
  }, [id, dispatch]);

  useEffect(() => {
    if (!counterparty?.id) return;
    const hasContragentChanged =
      previousDocumentsContragentIdRef.current !== null &&
      previousDocumentsContragentIdRef.current !== counterparty.id;

    previousDocumentsContragentIdRef.current = counterparty.id;

    if (hasContragentChanged && documentsPage !== 0) {
      setDocumentsPage(0);
      return;
    }

    dispatch(
      fetchRequiredDocuments({
        contragentId: counterparty.id,
        size: documentsRowsPerPage,
        page: documentsPage,
      })
    );
  }, [counterparty?.id, dispatch, documentsPage, documentsRowsPerPage]);

  useEffect(() => {
    if (!counterparty?.id) return;

    const hasContragentChanged =
      previousApplicationsContragentIdRef.current !== null &&
      previousApplicationsContragentIdRef.current !== counterparty.id;

    previousApplicationsContragentIdRef.current = counterparty.id;

    if (hasContragentChanged && applicationsStatusFilter !== StatusFilterValue.All) {
      setApplicationsStatusFilter(StatusFilterValue.All);
      return;
    }

    if (hasContragentChanged && applicationsPage !== 0) {
      setApplicationsPage(0);
      return;
    }

    dispatch(
      fetchApplications({
        contragentId: counterparty.id,
        page: applicationsPage,
        size: applicationsRowsPerPage,
        status:
          applicationsStatusFilter === StatusFilterValue.All ? undefined : applicationsStatusFilter,
      })
    );
  }, [
    applicationsPage,
    applicationsRowsPerPage,
    applicationsStatusFilter,
    counterparty?.id,
    dispatch,
  ]);

  useEffect(() => {
    if (!counterparty?.id) return;

    const hasContragentChanged =
      previousActiveApplicationsContragentIdRef.current !== null &&
      previousActiveApplicationsContragentIdRef.current !== counterparty.id;

    previousActiveApplicationsContragentIdRef.current = counterparty.id;

    if (hasContragentChanged && activeApplicationsPage !== 0) {
      setActiveApplicationsPage(0);
      return;
    }

    dispatch(
      fetchActiveApplications({
        contragentId: counterparty.id,
        page: activeApplicationsPage,
        size: activeApplicationsRowsPerPage,
      })
    );
  }, [activeApplicationsPage, activeApplicationsRowsPerPage, counterparty?.id, dispatch]);

  if (!id) {
    return <Navigate to="/covenants/get-started" replace />;
  }

  if (cpStatus === 'success' && counterparty === null) {
    return <Navigate to="/covenants/get-started" replace />;
  }

  const handleCreateNew = async () => {
    if (!counterparty?.id) return;

    try {
      const { id: applicationId } = await dispatch(
        createApplication({
          contragentId: counterparty.id,
        })
      ).unwrap();

      setIsCreateModalOpen(false);
      persistCovenantsApplicationContragent(applicationId, counterparty.id);
      navigate(buildCovenantsApplicationPath(applicationId, counterparty.id), {
        state: { contragentId: counterparty.id },
      });
    } catch (error) {
      setModalErrorText(
        getErrorMessage(error as { message?: string; data?: { message?: string } })
      );
    }
  };

  const handleCreateClick = async () => {
    if (activeAppsLoading) return;

    if (activeApplicationsPageConfiguration.totalElements > 0) {
      setActiveApplicationsPage(0);
      setIsCreateModalOpen(true);
    } else {
      await handleCreateNew();
    }
  };

  if (
    cpLoading ||
    isInitialDocumentsLoading ||
    isInitialApplicationsLoading ||
    isInitialActiveApplicationsLoading
  ) {
    return (
      <Box sx={commonStyles.centeredLoader}>
        <CircularProgress />
      </Box>
    );
  }

  if (cpError) {
    return <ModalAlert isOpen text={getErrorMessage(cpError)} onClose={closeErrorModal} />;
  }

  if (!counterparty) return null;

  return (
    <Box sx={[commonStyles.pageRoot, clientStyles.pageRoot]}>
      <Container sx={clientStyles.pageContainer}>
        <Box sx={clientStyles.pageHeader}>
          <Typography sx={clientStyles.pageTitle}>{counterparty.displayName}</Typography>
          <Typography sx={clientStyles.pageCode}>ЄДРПОУ: {counterparty.code}</Typography>
        </Box>
        <Divider sx={clientStyles.pageHeaderDivider} />

        <Box sx={commonStyles.content}>
          <DocumentsView
            contragentId={counterparty.id}
            documents={documents}
            page={documentsPage}
            rowsPerPage={documentsRowsPerPage}
            pageConfiguration={documentsPageConfiguration}
            isLoading={docsLoading}
            onPageChange={setDocumentsPage}
            onRowsPerPageChange={(nextRowsPerPage) => {
              setDocumentsRowsPerPage(nextRowsPerPage);
              setDocumentsPage(0);
            }}
            onCreateApplication={handleCreateClick}
          />
          <ApplicationTableView
            applications={applications}
            page={applicationsPage}
            rowsPerPage={applicationsRowsPerPage}
            pageConfiguration={applicationsPageConfiguration}
            isLoading={appsLoading}
            statusFilter={applicationsStatusFilter}
            onStatusFilterChange={(nextStatus) => {
              setApplicationsStatusFilter(nextStatus);
              setApplicationsPage(0);
            }}
            onPageChange={setApplicationsPage}
            onRowsPerPageChange={(nextRowsPerPage) => {
              setApplicationsRowsPerPage(nextRowsPerPage);
              setApplicationsPage(0);
            }}
            onViewApplication={(app) => {
              persistCovenantsApplicationContragent(String(app.id), counterparty.id);
              navigate(buildCovenantsApplicationPath(app.id, counterparty.id), {
                state: { contragentId: counterparty.id },
              });
            }}
          />
        </Box>
      </Container>

      <CreateApplicationModal
        open={isCreateModalOpen}
        onClose={() => setIsCreateModalOpen(false)}
        onCreateNew={handleCreateNew}
        onEditExisting={(appId: string) => {
          setIsCreateModalOpen(false);
          persistCovenantsApplicationContragent(appId, counterparty.id);
          navigate(buildCovenantsApplicationPath(appId, counterparty.id), {
            state: { contragentId: counterparty.id },
          });
        }}
        activeApplications={activeApplications}
        activeApplicationsPage={activeApplicationsPage}
        activeApplicationsPageConfiguration={activeApplicationsPageConfiguration}
        isLoading={activeAppsLoading}
        onPageChange={(nextPage) => setActiveApplicationsPage(nextPage)}
      />

      <ModalAlert isOpen={!!modalErrorText} text={modalErrorText} onClose={closeErrorModal} />
    </Box>
  );
};

export default CovenantsApplicationByClient;
